import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, defer, firstValueFrom, from, map, timeout } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../auth/services/auth.service';
import { ExtraMetadataVersionSplit, SplitsRepository } from './splits.repository';
import {
  ColaSincronizacionSplitsService,
  EstadoSplitsPersistido
} from '../../services/cola-sincronizacion-splits.service';
import {
  ContratoSplitEntity,
  ContratoSplitCompleto,
  VersionSplitEntity,
  ColaboradorSplitEntity,
  CondicionesEconomicasEntity,
  EventoTrazabilidadEntity,
  ParticipanteDirectorioEntity,
  CatalogosSplitsMml,
  ESTUDIOS_EPG_MML,
  TIPOS_DOCUMENTO_CATALOGO_DEFAULT,
  normalizarNumeroDocumento,
  normalizarRolSplitOficial,
  normalizarTipoDocumento,
  generarCodigoSesionConsecutivo,
  obtenerFechaHoraActualFormateada
} from '../models/splits.model';

const LEGACY_STORAGE_KEYS_SPLITS = [
  'mj_splits_mml_estado_v1',
  'mj_splits_mml_estado_v2',
  'mj_splits_mml_outbox_v1',
  'mj_splits_mml_sync_trace_v1'
];
const BROADCAST_CHANNEL_SPLITS = 'mj_splits_mml_sync_v2';
const READ_TIMEOUT_MS = 15_000;

function crearCatalogosDefault(): CatalogosSplitsMml {
  return {
    estudios: ESTUDIOS_EPG_MML.map((nombre, idx) => ({
      id: idx + 1,
      nombre,
      direccion: 'Medellín'
    })),
    tiposDocumento: [...TIPOS_DOCUMENTO_CATALOGO_DEFAULT]
  };
}

function crearEstadoVacio(): EstadoSplitsPersistido {
  return {
    contratos: [],
    colaboradores: [],
    directorioParticipantes: [],
    versionesPorContrato: {},
    trazabilidadPorContrato: {},
    contratosCompletosPorId: {},
    catalogos: crearCatalogosDefault()
  };
}

@Injectable({
  providedIn: 'root'
})
export class HttpSplitsRepository extends SplitsRepository {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly colaSync = inject(ColaSincronizacionSplitsService);
  private readonly apiUrl = environment.URL_SERVER.endsWith('/')
    ? `${environment.URL_SERVER}mml-splits`
    : `${environment.URL_SERVER}/mml-splits`;

  private readonly estado$ = new BehaviorSubject<EstadoSplitsPersistido>(crearEstadoVacio());
  private canalBroadcast?: BroadcastChannel;
  private sincronizandoLectura = false;

  constructor() {
    super();

    this.limpiarCacheLegacyNavegador();

    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.canalBroadcast = new BroadcastChannel(BROADCAST_CHANNEL_SPLITS);
      this.canalBroadcast.onmessage = () => {
        void this.recargarSnapshotLocalYSincronizar();
      };
    }

    this.colaSync.onPostSincronizacion(() => {
      void this.sincronizarDesdeBackend();
      this.notificarOtrasPestanas();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        void this.sincronizarDesdeBackend();
      });
      window.addEventListener('focus', () => {
        if (typeof navigator === 'undefined' || navigator.onLine) {
          void this.sincronizarDesdeBackend();
        }
      });
    }

    let primeraEmisionUser = true;
    this.authService.user$.subscribe(() => {
      if (primeraEmisionUser) {
        primeraEmisionUser = false;
        return;
      }
      void this.sincronizarDesdeBackend();
    });

    void this.inicializarRepositorio();
  }

  private obtenerActorActual(): string {
    const u = this.authService.getUser();
    if (u) {
      const nombre = [u.nombre, u.apellido, u.first_name, u.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (nombre && u.email) return `${nombre} (${u.email})`;
      if (nombre) return nombre;
      if (u.email) return String(u.email);
    }
    return 'Equipo Medellín Music Lab';
  }

  private async inicializarRepositorio(): Promise<void> {
    const snapshotIdb = await this.colaSync.cargarSnapshotOffline();
    if (snapshotIdb) {
      const conCola = this.colaSync.aplicarColaPendienteSobreEstado({
        contratos: Array.isArray(snapshotIdb.contratos) ? snapshotIdb.contratos : [],
        colaboradores: Array.isArray(snapshotIdb.colaboradores) ? snapshotIdb.colaboradores : [],
        directorioParticipantes: Array.isArray(snapshotIdb.directorioParticipantes)
          ? snapshotIdb.directorioParticipantes
          : [],
        versionesPorContrato: snapshotIdb.versionesPorContrato || {},
        trazabilidadPorContrato: snapshotIdb.trazabilidadPorContrato || {},
        contratosCompletosPorId: snapshotIdb.contratosCompletosPorId || {},
        catalogos: snapshotIdb.catalogos || crearCatalogosDefault()
      });
      this.estado$.next(conCola);
    } else if (this.colaSync.tieneCambiosPendientes()) {
      const conCola = this.colaSync.aplicarColaPendienteSobreEstado(crearEstadoVacio());
      this.estado$.next(conCola);
    }

    if (typeof navigator === 'undefined' || navigator.onLine) {
      await this.sincronizarDesdeBackend();
    }
  }

  private async recargarSnapshotLocalYSincronizar(): Promise<void> {
    const snapshotIdb = await this.colaSync.cargarSnapshotOffline();
    if (snapshotIdb) {
      const conCola = this.colaSync.aplicarColaPendienteSobreEstado(snapshotIdb);
      this.estado$.next(conCola);
    }
    if (typeof navigator === 'undefined' || navigator.onLine) {
      await this.sincronizarDesdeBackend();
    }
  }

  private limpiarCacheLegacyNavegador(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      for (const legacyKey of LEGACY_STORAGE_KEYS_SPLITS) {
        window.localStorage.removeItem(legacyKey);
      }
    } catch {
      // Ignorar si el navegador restringe el acceso a localStorage
    }
  }

  private notificarOtrasPestanas(): void {
    if (!this.canalBroadcast) return;
    try {
      this.canalBroadcast.postMessage({ ts: Date.now() });
    } catch {
      // Ignorar errores de canal cerrado
    }
  }

  private emitirYGuardarSnapshot(nuevo: EstadoSplitsPersistido, notificarPestanas = true): void {
    const estadoConCola = this.colaSync.aplicarColaPendienteSobreEstado(nuevo);
    this.estado$.next(estadoConCola);
    void this.colaSync.guardarSnapshotOffline(estadoConCola);
    if (notificarPestanas) {
      this.notificarOtrasPestanas();
    }
  }

  async sincronizarDesdeBackend(): Promise<void> {
    if (this.sincronizandoLectura) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    this.sincronizandoLectura = true;
    try {
      const resp = await firstValueFrom(
        this.http
          .get<{
            success?: boolean;
            data?: {
              tablasCreadas?: boolean;
              catalogos?: CatalogosSplitsMml;
              datos?: {
                contratos?: ContratoSplitEntity[];
                colaboradores?: ColaboradorSplitEntity[];
                directorioParticipantes?: ParticipanteDirectorioEntity[];
              };
            };
          }>(`${this.apiUrl}/estado?incluirEliminados=true`)
          .pipe(timeout(READ_TIMEOUT_MS))
      );

      const payload = resp?.data;
      if (payload) {
        const actual = this.estado$.value;
        const catalogosRemotos: CatalogosSplitsMml = {
          estudios:
            Array.isArray(payload.catalogos?.estudios) && payload.catalogos!.estudios.length > 0
              ? payload.catalogos!.estudios
              : actual.catalogos?.estudios || crearCatalogosDefault().estudios,
          tiposDocumento:
            Array.isArray(payload.catalogos?.tiposDocumento) &&
            payload.catalogos!.tiposDocumento.length > 0
              ? payload.catalogos!.tiposDocumento
              : actual.catalogos?.tiposDocumento || crearCatalogosDefault().tiposDocumento
        };

        if (payload.tablasCreadas && payload.datos) {
          const baseRemota: EstadoSplitsPersistido = {
            ...actual,
            contratos: Array.isArray(payload.datos.contratos) ? payload.datos.contratos : [],
            colaboradores: Array.isArray(payload.datos.colaboradores)
              ? payload.datos.colaboradores
              : [],
            directorioParticipantes: Array.isArray(payload.datos.directorioParticipantes)
              ? payload.datos.directorioParticipantes
              : [],
            catalogos: catalogosRemotos
          };
          this.emitirYGuardarSnapshot(baseRemota, false);
        } else {
          this.emitirYGuardarSnapshot(
            {
              ...actual,
              catalogos: catalogosRemotos
            },
            false
          );
        }
      }
    } catch {
      // En modo sin conexión o intermitencia de red, se mantiene el snapshot local + cola sin romper la UI
    } finally {
      this.sincronizandoLectura = false;
    }
  }

  override getCatalogos(): Observable<CatalogosSplitsMml> {
    return this.estado$.pipe(
      map((estado) => estado.catalogos || crearCatalogosDefault())
    );
  }

  override getContratos(incluirEliminados: boolean = false): Observable<ContratoSplitEntity[]> {
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.sincronizarDesdeBackend();
    }
    return this.estado$.pipe(
      map((estado) => {
        const lista = incluirEliminados
          ? [...estado.contratos]
          : estado.contratos.filter((c) => !c.eliminado);
        return lista.sort((a, b) => (b.actualizadoEn || '').localeCompare(a.actualizadoEn || ''));
      })
    );
  }

  override getDirectorioParticipantes(): Observable<ParticipanteDirectorioEntity[]> {
    return this.estado$.pipe(
      map((estado) =>
        [...estado.directorioParticipantes].sort((a, b) =>
          (a.nombreCompleto || '').localeCompare(b.nombreCompleto || '')
        )
      )
    );
  }

  override getTodosColaboradoresActivos(): Observable<ColaboradorSplitEntity[]> {
    return this.estado$.pipe(
      map((estado) => estado.colaboradores.filter((c) => !c.eliminado))
    );
  }

  override getContratoCompleto(id: string): Observable<ContratoSplitCompleto | undefined> {
    return defer(() => from(this.obtenerContratoCompletoResiliente(id)));
  }

  private async obtenerContratoCompletoResiliente(id: string): Promise<ContratoSplitCompleto | undefined> {
    if (!id) return undefined;

    const pendienteEnCola = this.colaSync.obtenerContratoPendienteEnCola(id);

    if (typeof navigator === 'undefined' || navigator.onLine) {
      try {
        const resp = await firstValueFrom(
          this.http
            .get<{
              success?: boolean;
              data?: ContratoSplitCompleto | null;
            }>(`${this.apiUrl}/contratos/${encodeURIComponent(id)}`)
            .pipe(timeout(READ_TIMEOUT_MS))
        );
        const remoto = resp?.data;
        if (remoto && remoto.contrato) {
          const actual = this.estado$.value;
          const contratos = [
            ...actual.contratos.filter((c) => c.id !== id),
            remoto.contrato
          ];
          const colaboradores = [
            ...actual.colaboradores.filter((c) => c.contratoId !== id),
            ...(remoto.colaboradores || [])
          ];

          const nuevoEstado: EstadoSplitsPersistido = {
            ...actual,
            contratos,
            colaboradores,
            versionesPorContrato: {
              ...actual.versionesPorContrato,
              [id]: remoto.versiones || []
            },
            trazabilidadPorContrato: {
              ...actual.trazabilidadPorContrato,
              [id]: remoto.eventosTrazabilidad || []
            },
            contratosCompletosPorId: {
              ...actual.contratosCompletosPorId,
              [id]: remoto
            }
          };

          this.emitirYGuardarSnapshot(nuevoEstado, false);

          if (pendienteEnCola) {
            return {
              ...pendienteEnCola,
              versiones: this.estado$.value.versionesPorContrato[id] || pendienteEnCola.versiones || [],
              eventosTrazabilidad:
                this.estado$.value.trazabilidadPorContrato[id] || pendienteEnCola.eventosTrazabilidad || []
            };
          }

          return remoto;
        }
      } catch {
        // Fallback a cola / snapshot IndexedDB
      }
    }

    if (pendienteEnCola) {
      return {
        ...pendienteEnCola,
        versiones: this.estado$.value.versionesPorContrato[id] || pendienteEnCola.versiones || [],
        eventosTrazabilidad:
          this.estado$.value.trazabilidadPorContrato[id] || pendienteEnCola.eventosTrazabilidad || []
      };
    }

    const estado = this.estado$.value;
    if (estado.contratosCompletosPorId?.[id]) {
      const guardado = estado.contratosCompletosPorId[id];
      return {
        ...guardado,
        versiones: estado.versionesPorContrato[id] || guardado.versiones || [],
        eventosTrazabilidad: estado.trazabilidadPorContrato[id] || guardado.eventosTrazabilidad || []
      };
    }

    const contrato = estado.contratos.find((c) => c.id === id);
    if (!contrato) return undefined;

    const colaboradores = estado.colaboradores
      .filter((c) => c.contratoId === id && !c.eliminado)
      .sort((a, b) => (a.orden || 0) - (b.orden || 0));

    const condicionesEconomicas: CondicionesEconomicasEntity = {
      id: `cond-${id}`,
      contratoId: id,
      moneda: 'COP',
      eliminado: false
    };

    return {
      contrato,
      colaboradores,
      editoras: [],
      condicionesEconomicas,
      versiones: estado.versionesPorContrato[id] || [],
      eventosTrazabilidad: estado.trazabilidadPorContrato[id] || []
    };
  }

  override async saveContratoCompleto(agregado: ContratoSplitCompleto): Promise<string> {
    const contratoId = agregado.contrato.id;
    const ahora = new Date().toISOString();

    const contratoActualizado: ContratoSplitEntity = {
      ...agregado.contrato,
      creadoEn: agregado.contrato.creadoEn || ahora,
      actualizadoEn: ahora
    };

    const actual = this.estado$.value;
    const eventosPrevios = actual.trazabilidadPorContrato[contratoId] || [];
    const eventosCombinados = [
      ...(agregado.eventosTrazabilidad || []),
      ...eventosPrevios.filter(
        (evPrev) => !(agregado.eventosTrazabilidad || []).some((e) => e.id === evPrev.id)
      )
    ];

    const agregadoNormalizado: ContratoSplitCompleto = {
      ...agregado,
      contrato: contratoActualizado,
      eventosTrazabilidad: eventosCombinados
    };

    const contratos = [
      ...actual.contratos.filter((c) => c.id !== contratoId),
      contratoActualizado
    ];
    const colaboradores = [
      ...actual.colaboradores.filter((c) => c.contratoId !== contratoId),
      ...(agregado.colaboradores || []).filter((c) => !c.eliminado)
    ];

    const mapaDirectorio = new Map<string, ParticipanteDirectorioEntity>();
    for (const d of actual.directorioParticipantes) {
      mapaDirectorio.set(normalizarNumeroDocumento(d.numeroDocumento || d.id), d);
    }

    for (const colab of agregado.colaboradores || []) {
      if (colab.eliminado) continue;
      const docNorm = normalizarNumeroDocumento(colab.numeroDocumento);
      const nombreLimpio = (colab.nombreCompleto || '').trim();
      if (docNorm && docNorm.length >= 5 && nombreLimpio) {
        const prev = mapaDirectorio.get(docNorm);
        mapaDirectorio.set(docNorm, {
          id: docNorm,
          tipoDocumento: normalizarTipoDocumento(colab.tipoDocumento || prev?.tipoDocumento),
          numeroDocumento: docNorm,
          nombreCompleto: nombreLimpio,
          nombreArtistico: colab.nombreArtistico?.trim() || prev?.nombreArtistico || '',
          email: colab.email?.trim() || prev?.email || '',
          telefono: colab.telefono?.trim() || prev?.telefono || '',
          rolSugerido: normalizarRolSplitOficial(colab.rolContribucion || prev?.rolSugerido),
          aporteSugerido: colab.aporteCreativo?.trim() || prev?.aporteSugerido || '',
          ipi: colab.ipi?.trim() || prev?.ipi || '',
          obraReferenciaCsv: prev?.obraReferenciaCsv || contratoActualizado.tituloObra || '',
          origen: prev?.origen || 'EXPEDIENTE',
          fechaImportacion: prev?.fechaImportacion || ahora,
          actualizadoEn: ahora
        });
      }
    }

    this.colaSync.encolarGuardarContrato(agregadoNormalizado);

    this.emitirYGuardarSnapshot({
      ...actual,
      contratos,
      colaboradores,
      directorioParticipantes: Array.from(mapaDirectorio.values()),
      trazabilidadPorContrato: {
        ...actual.trazabilidadPorContrato,
        [contratoId]: eventosCombinados
      },
      contratosCompletosPorId: {
        ...actual.contratosCompletosPorId,
        [contratoId]: agregadoNormalizado
      }
    });

    return contratoId;
  }

  override async deleteContrato(id: string): Promise<void> {
    const ahora = new Date().toISOString();
    const actual = this.estado$.value;
    const contratoObj = actual.contratos.find((c) => c.id === id);

    this.colaSync.encolarEliminarContrato(id, contratoObj?.tituloObra);

    this.emitirYGuardarSnapshot({
      ...actual,
      contratos: actual.contratos.map((c) =>
        c.id === id ? { ...c, eliminado: true, actualizadoEn: ahora } : c
      )
    });
  }

  override async restoreContrato(id: string): Promise<void> {
    const ahora = new Date().toISOString();
    const actual = this.estado$.value;

    this.colaSync.encolarRestaurarContrato(id);

    this.emitirYGuardarSnapshot({
      ...actual,
      contratos: actual.contratos.map((c) =>
        c.id === id ? { ...c, eliminado: false, actualizadoEn: ahora } : c
      )
    });
  }

  override async duplicateContrato(id: string, nuevoTitulo: string): Promise<string> {
    const original = await this.obtenerContratoCompletoResiliente(id);
    if (!original) {
      throw new Error('No se encontró el Split Sheet original para duplicar');
    }

    const ahora = new Date().toISOString();
    const sufijo = Math.floor(1000 + Math.random() * 9000);
    const nuevoId = generarCodigoSesionConsecutivo(this.estado$.value.contratos);

    const nuevosColabs: ColaboradorSplitEntity[] = (original.colaboradores || [])
      .filter((c) => !c.eliminado)
      .map((c, idx) => ({
        ...c,
        id: `colab-${nuevoId}-${idx + 1}-${Math.random().toString(36).slice(2, 6)}`,
        contratoId: nuevoId,
        orden: idx + 1,
        firma: undefined,
        eliminado: false
      }));

    const colabProdIdx = original.colaboradores.findIndex((c) => c.esProductorFonografico);
    const nuevoColabProd = nuevosColabs[colabProdIdx >= 0 ? colabProdIdx : 0] || nuevosColabs[0];

    const prodOriginal = original.contrato.productorFonografico;
    const nuevoProd = prodOriginal
      ? {
          ...prodOriginal,
          colaboradorIdDesignado:
            prodOriginal.colaboradorIdDesignado === 'TERCERO'
              ? 'TERCERO'
              : nuevoColabProd?.id || '',
          firmaProductor: undefined
        }
      : undefined;

    const eventoDuplicacion: EventoTrazabilidadEntity = {
      id: `evt-${nuevoId}-1`,
      contratoId: nuevoId,
      tipoEvento: 'CREACION',
      tituloEvento: 'Split Sheet creado por duplicación',
      descripcion: `Copia generada a partir de la sesión ${original.contrato.codigoSesion || id}.`,
      usuario: this.obtenerActorActual(),
      timestamp: ahora
    };

    const copiaCompleta: ContratoSplitCompleto = {
      contrato: {
        ...original.contrato,
        id: nuevoId,
        codigoSesion: nuevoId,
        fechaHoraSesion: obtenerFechaHoraActualFormateada(),
        tituloObra: nuevoTitulo,
        estado: 'BORRADOR',
        bloqueadoEdicion: false,
        fechaBloqueoEdicion: undefined,
        motivoBloqueoEdicion: undefined,
        archivoFirmado: undefined,
        archivosFirmadosHistorico: [],
        productorFonografico: nuevoProd,
        creadoEn: ahora,
        actualizadoEn: ahora,
        eliminado: false
      },
      colaboradores: nuevosColabs,
      editoras: [],
      condicionesEconomicas: {
        id: `cond-${nuevoId}`,
        contratoId: nuevoId,
        moneda: original.condicionesEconomicas?.moneda || 'COP',
        eliminado: false
      },
      versiones: [],
      eventosTrazabilidad: [eventoDuplicacion]
    };

    await this.saveContratoCompleto(copiaCompleta);
    return nuevoId;
  }

  override async createVersionSnapshot(
    contratoId: string,
    markdown: string,
    autor: string,
    motivo: string,
    extraMeta?: ExtraMetadataVersionSplit
  ): Promise<VersionSplitEntity> {
    const ahora = new Date().toISOString();
    const actual = this.estado$.value;
    const prev = actual.versionesPorContrato[contratoId] || [];
    const versionNumero = prev.length + 1;
    const idVersion = `ver-${contratoId}-v${versionNumero}-${Date.now().toString(36)}`;

    const versionLocal: VersionSplitEntity = {
      id: idVersion,
      contratoId,
      versionNumero,
      markdownSnapshot: markdown,
      autorCambio: autor,
      motivo,
      archivoUrl: extraMeta?.archivoUrl,
      storageKey: extraMeta?.storageKey,
      nombreArchivo: extraMeta?.nombreArchivo,
      tamanoBytes: extraMeta?.tamanoBytes,
      pendienteSubidaS3: extraMeta?.pendienteSubidaS3,
      cambios: extraMeta?.cambios,
      creadoEn: ahora
    };

    this.colaSync.encolarVersionSnapshot({
      id: idVersion,
      contratoId,
      markdown,
      autor,
      motivo,
      archivoUrl: extraMeta?.archivoUrl,
      storageKey: extraMeta?.storageKey,
      nombreArchivo: extraMeta?.nombreArchivo,
      tamanoBytes: extraMeta?.tamanoBytes,
      idArchivoCola: extraMeta?.idArchivoCola,
      pendienteSubidaS3: extraMeta?.pendienteSubidaS3,
      cambios: extraMeta?.cambios,
      creadoEn: ahora
    });

    this.emitirYGuardarSnapshot({
      ...actual,
      versionesPorContrato: {
        ...actual.versionesPorContrato,
        [contratoId]: [versionLocal, ...prev.filter((v) => v.id !== idVersion)]
      }
    });

    return versionLocal;
  }

  override getVersiones(contratoId: string): Observable<VersionSplitEntity[]> {
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.cargarVersionesDesdeBackend(contratoId);
    }
    return this.estado$.pipe(
      map((estado) => estado.versionesPorContrato[contratoId] || [])
    );
  }

  private async cargarVersionesDesdeBackend(contratoId: string): Promise<void> {
    if (!contratoId) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    try {
      const resp = await firstValueFrom(
        this.http
          .get<{ success?: boolean; data?: VersionSplitEntity[] }>(
            `${this.apiUrl}/contratos/${encodeURIComponent(contratoId)}/versiones`
          )
          .pipe(timeout(READ_TIMEOUT_MS))
      );
      if (Array.isArray(resp?.data)) {
        const actual = this.estado$.value;
        this.emitirYGuardarSnapshot(
          {
            ...actual,
            versionesPorContrato: {
              ...actual.versionesPorContrato,
              [contratoId]: resp.data
            }
          },
          false
        );
      }
    } catch {
      // Silencioso en lectura secundaria
    }
  }

  override async registrarEventoTrazabilidad(evento: EventoTrazabilidadEntity): Promise<void> {
    const actual = this.estado$.value;
    const prev = actual.trazabilidadPorContrato[evento.contratoId] || [];

    this.colaSync.encolarEventoTrazabilidad(evento);

    this.emitirYGuardarSnapshot({
      ...actual,
      trazabilidadPorContrato: {
        ...actual.trazabilidadPorContrato,
        [evento.contratoId]: [evento, ...prev.filter((e) => e.id !== evento.id)]
      }
    });
  }

  override getEventosTrazabilidad(contratoId: string): Observable<EventoTrazabilidadEntity[]> {
    if (typeof navigator === 'undefined' || navigator.onLine) {
      void this.cargarEventosTrazabilidadDesdeBackend(contratoId);
    }
    return this.estado$.pipe(
      map((estado) => estado.trazabilidadPorContrato[contratoId] || [])
    );
  }

  private async cargarEventosTrazabilidadDesdeBackend(contratoId: string): Promise<void> {
    if (!contratoId) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    try {
      const resp = await firstValueFrom(
        this.http
          .get<{ success?: boolean; data?: EventoTrazabilidadEntity[] }>(
            `${this.apiUrl}/contratos/${encodeURIComponent(contratoId)}/trazabilidad`
          )
          .pipe(timeout(READ_TIMEOUT_MS))
      );
      if (Array.isArray(resp?.data)) {
        const actual = this.estado$.value;
        this.emitirYGuardarSnapshot(
          {
            ...actual,
            trazabilidadPorContrato: {
              ...actual.trazabilidadPorContrato,
              [contratoId]: resp.data
            }
          },
          false
        );
      }
    } catch {
      // Silencioso en lectura secundaria
    }
  }

  override async upsertParticipantesDirectorio(
    items: ParticipanteDirectorioEntity[]
  ): Promise<{ nuevos: number; actualizados: number }> {
    const actual = this.estado$.value;
    const mapa = new Map<string, ParticipanteDirectorioEntity>();
    for (const d of actual.directorioParticipantes) {
      const doc = normalizarNumeroDocumento(d.numeroDocumento || d.id);
      if (doc) mapa.set(doc, d);
    }

    let nuevos = 0;
    let actualizados = 0;
    const ahora = new Date().toISOString();

    for (const item of items) {
      const docNorm = normalizarNumeroDocumento(item.numeroDocumento || item.id);
      if (!docNorm || !item.nombreCompleto?.trim()) continue;
      if (mapa.has(docNorm)) {
        actualizados++;
      } else {
        nuevos++;
      }
      mapa.set(docNorm, {
        ...item,
        id: docNorm,
        numeroDocumento: docNorm,
        actualizadoEn: ahora
      });
    }

    this.colaSync.encolarUpsertDirectorio(items);

    this.emitirYGuardarSnapshot({
      ...actual,
      directorioParticipantes: Array.from(mapa.values())
    });

    return { nuevos, actualizados };
  }

  override async eliminarParticipanteDirectorio(id: string): Promise<void> {
    const docNorm = normalizarNumeroDocumento(id);
    if (!docNorm) return;

    this.colaSync.encolarEliminarDirectorio(docNorm);

    const actual = this.estado$.value;
    this.emitirYGuardarSnapshot({
      ...actual,
      directorioParticipantes: actual.directorioParticipantes.filter(
        (d) => normalizarNumeroDocumento(d.numeroDocumento || d.id) !== docNorm
      )
    });
  }

  override async buscarParticipanteExternoPorDocumento(numeroDocumento: string): Promise<{
    encontrado: boolean;
    fuente?: string;
    participante: ParticipanteDirectorioEntity | null;
  }> {
    const docNorm = normalizarNumeroDocumento(numeroDocumento);
    if (!docNorm || docNorm.length < 5) {
      return { encontrado: false, participante: null };
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return { encontrado: false, participante: null };
    }

    try {
      const resp = await firstValueFrom(
        this.http
          .get<{
            success?: boolean;
            data?: {
              encontrado: boolean;
              fuente?: string;
              participante: ParticipanteDirectorioEntity | null;
            };
          }>(`${this.apiUrl}/buscar-participante/${encodeURIComponent(docNorm)}`)
          .pipe(timeout(8_000))
      );
      if (resp?.data?.encontrado && resp.data.participante) {
        return resp.data;
      }
    } catch {
      // Sin conexión o no encontrado
    }

    return { encontrado: false, participante: null };
  }
}
